// Relative paths, not the `@graphql-api` alias: shared/ui-library's StreetView
// imports services/api, so the login app compiles and bundles this file too,
// and login maps `@graphql-api` to a directory.
import {
  executeGraphQL,
  executeGraphQLWithExtensions,
  type GraphQLExtensions
} from '../../../shared/graphql-api/src/executeGraphQL.js'
import { getCurrentToken } from '../../../shared/graphql-api/src/index.js'
import { graphql } from '../gql/gql.js'
import type {
  AgeBand,
  EpcRating,
  FeConfigQuery,
  PropertyDetailQuery,
  PropertyForm,
  PropertyMapMarkersPageQuery,
  PropertyOrderCondition,
  PropertyOverlayQuery,
  PropertyPvQuery,
  PropertyType,
  PropertyWhere,
  Sort_Order
} from '../gql/graphql.js'
import {
  extractInspectionData,
  processTableProperties
} from '../utils/propertyUtils.js'
import {
  enhanceMockProperty,
  mockProperties,
  mockPropertyLists
} from './mockData.js'

const PROPERTY_LISTS_QUERY = graphql(`
  query PropertyLists($where: PropertyListWhere) {
    propertyLists(where: $where) {
      id
      name
    }
  }
`)

const PROPERTY_TABLE_PAGE_QUERY = graphql(`
  query PropertyTablePage($where: PropertyWhere!, $paging: Paging!) {
    properties(paging: $paging, where: $where) {
      id
      type
      address(where: { active: true }, paging: { limit: 1 }) {
        combined
      }
      units(where: { active: true }, paging: { limit: 1 }) {
        form
        age
      }
      kestrixInspections: engPerfInspections(
        where: { sourceIn: [KESTRIX] }
        paging: { limit: 3 }
      ) {
        inspectionDate
        source
        spaceHeatingDemand
        currentRating
      }
      epcInspections: engPerfInspections(
        where: { sourceIn: [PROPECO] }
        paging: { limit: 3 }
      ) {
        inspectionDate
        source
        spaceHeatingDemand
        currentRating
      }
      predictedInspections: engPerfInspections(
        where: { sourceIn: [KESTRIX_SAP_COMPARATOR] }
        paging: { limit: 3 }
      ) {
        inspectionDate
        source
        currentRating
      }
      models(where: { model: { active: true } }, paging: { limit: 1 }) {
        modelId
        model {
          id
          active
        }
      }
    }
  }
`)

const PROPERTY_MAP_MARKERS_PAGE_QUERY = graphql(`
  query PropertyMapMarkersPage($where: PropertyWhere!, $paging: Paging!) {
    properties(paging: $paging, where: $where) {
      id
      address(where: { active: true }, paging: { limit: 1 }) {
        combined
        latlng
      }
      epcInspections: engPerfInspections(
        where: { sourceIn: [PROPECO] }
        paging: { limit: 3 }
      ) {
        currentRating
        inspectionDate
      }
      predictedInspections: engPerfInspections(
        where: { sourceIn: [KESTRIX_SAP_COMPARATOR] }
        paging: { limit: 3 }
      ) {
        currentRating
        inspectionDate
      }
      models(where: { model: { active: true } }, paging: { limit: 1 }) {
        modelId
      }
    }
  }
`)

const PROPERTY_OVERLAY_QUERY = graphql(`
  query PropertyOverlay($where: PropertyWhereUnique!) {
    property(where: $where) {
      id
      type
      address(where: { active: true }, paging: { limit: 1 }) {
        combined
        latlng
        uprn
      }
      units(where: { active: true }, paging: { limit: 1 }) {
        form
        age
      }
      kestrixInspections: engPerfInspections(
        where: { sourceIn: [KESTRIX] }
        paging: { limit: 1 }
      ) {
        inspectionDate
        source
        spaceHeatingDemand
        currentRating
      }
      epcInspections: engPerfInspections(
        where: { sourceIn: [PROPECO] }
        paging: { limit: 1 }
      ) {
        inspectionDate
        source
        spaceHeatingDemand
        currentRating
      }
      predictedInspections: engPerfInspections(
        where: { sourceIn: [KESTRIX_SAP_COMPARATOR] }
        paging: { limit: 1 }
      ) {
        inspectionDate
        source
        currentRating
      }
      models(where: { model: { active: true } }, paging: { limit: 1 }) {
        modelId
        model {
          id
          active
          recommendations {
            parentRecommendationId
            shdDeltaAfterUpgrade
            retrofitAction {
              displayName
              subjectType
            }
          }
        }
      }
    }
  }
`)

/** No nested paging on `models`: some backends answer that shape with a 400. */
const PROPERTY_DETAIL_QUERY = graphql(`
  query PropertyDetail($where: PropertyWhereUnique!) {
    property(where: $where) {
      id
      type
      address(where: { active: true }, paging: { limit: 1 }) {
        uprn
        combined
        latlng
      }
      units(where: { active: true }, paging: { limit: 1 }) {
        form
        age
      }
      kestrixInspections: engPerfInspections(
        where: { sourceIn: [KESTRIX] }
        paging: { limit: 3 }
      ) {
        inspectionDate
        source
        spaceHeatingDemand
        currentRating
      }
      epcInspections: engPerfInspections(
        where: { sourceIn: [PROPECO] }
        paging: { limit: 3 }
      ) {
        inspectionDate
        source
        spaceHeatingDemand
        currentRating
      }
      models(where: { model: { active: true } }) {
        model {
          id
          active
          files {
            glb
          }
          annotations {
            active
            type
            category
            size
          }
          recommendations {
            parentRecommendationId
            shdDeltaAfterUpgrade
            retrofitAction {
              displayName
              subjectType
            }
          }
        }
      }
    }
  }
`)

const PROPERTY_PV_QUERY = graphql(`
  query PropertyPv($where: PropertyWhereUnique!) {
    property(where: $where) {
      pv {
        numPanels
        kWp
        annualOutputKwh
      }
    }
  }
`)

const FE_CONFIG_QUERY = graphql(`
  query FeConfig {
    feConfig {
      environment
      streetviewApiKey
      locale
      mapboxAccessToken
      gcpProjectId
      osApiKey
    }
  }
`)

/** UI filter state that {@link constructPropertyWhere} turns into a `PropertyWhere`. */
export type PropertyFilters = {
  readonly propertyList?: string
  readonly propertyTypes?: readonly PropertyType[]
  readonly propertyForms?: readonly PropertyForm[]
  readonly ageBands?: readonly AgeBand[]
  readonly epcRatings?: readonly EpcRating[]
  /** `'true'`/`'false'` from a select; `''` means no filter. */
  readonly hasModel?: '' | 'true' | 'false'
  /** Older boolean form of `hasModel`. Only `true` adds a filter. */
  readonly has3D?: boolean
  readonly addressSearch?: string
}

export type PropertyListOption = {
  readonly value: string
  readonly label: string
}

export type PropertyPage<TProperty> = {
  readonly properties: TProperty[]
  readonly totalCount: number
  readonly hasMore: boolean
}

type MapMarkerProperty = NonNullable<
  NonNullable<PropertyMapMarkersPageQuery['properties']>[number]
>
type PropertyDetail = NonNullable<PropertyDetailQuery['property']>
type PropertyPv = NonNullable<
  NonNullable<PropertyPvQuery['property']>['pv']
>
type FrontendConfig = NonNullable<FeConfigQuery['feConfig']>
type PropertyOverlayProperty = NonNullable<PropertyOverlayQuery['property']>

/** Shape of {@link fetchPropertyForOverlay}'s result. */
export type PropertyOverlay = {
  readonly id: string
  readonly type: PropertyOverlayProperty['type']
  readonly address: string
  readonly epcRating: string
  readonly predictedEpcRating: string
  readonly kestrixSpaceHeatingDemand: number | string
  readonly epcSpaceHeatingDemand: number | string
  readonly modelId: string | null
  readonly fullProperty: Pick<
    PropertyOverlayProperty,
    | 'address'
    | 'kestrixInspections'
    | 'epcInspections'
    | 'predictedInspections'
    | 'models'
    | 'id'
    | 'type'
    | 'units'
  >
}

const emptyPage = <TProperty>(): PropertyPage<TProperty> => ({
  properties: [],
  totalCount: 0,
  hasMore: false
})

const bearerAuth = (): { auth: { kind: 'bearer'; token: string } } => ({
  auth: { kind: 'bearer', token: getCurrentToken() }
})

const isPresent = <T>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined

/**
 * Read the page metadata the API puts in `extensions.page_info`.
 *
 * @param extensions - Response extensions from a paged query.
 * @returns The total row count and whether another page exists.
 */
const readPageInfo = (
  extensions: GraphQLExtensions
): { totalCount: number; hasMore: boolean } => ({
  totalCount: extensions.page_info?.total_count ?? 0,
  hasMore: extensions.page_info?.has_next_page ?? false
})

/**
 * Builds the `PropertyWhere` for a paged property query, sort included.
 *
 * @param filters - UI filter state.
 * @param sortField - Order condition, or null for the API default.
 * @param sortDirection - Sort direction, or null for the API default.
 * @returns The where clause with `sortBy` and `sortDirection` set when given.
 */
const constructSortedPropertyWhere = (
  filters: PropertyFilters,
  sortField: PropertyOrderCondition | null,
  sortDirection: Sort_Order | null
): PropertyWhere => {
  const where = constructPropertyWhere(filters)
  if (sortField) {
    where.sortBy = sortField
  }
  if (sortDirection) {
    where.sortDirection = sortDirection
  }
  return where
}

/**
 * Returns mock data when the app runs without a real API, otherwise throws.
 *
 * @param mockDataFunction - Produces the mock data.
 * @param functionName - Name used in the log line and error message.
 * @returns The mock data.
 * @throws {Error} When the app has a real API endpoint configured.
 */
const handleMockDataFallback = <T>(
  mockDataFunction: () => T,
  functionName: string
): T => {
  if (
    import.meta.env.VITE_USE_MOCK_AUTH === 'true' ||
    !import.meta.env.VITE_API_ENDPOINT
  ) {
    console.log(`Using mock data for ${functionName}`)
    return mockDataFunction()
  }
  throw new Error(`Mock data not available for ${functionName}`)
}

/**
 * Converts UI filter state into a GraphQL `PropertyWhere`.
 *
 * Used by every property fetch here and by the CSV export.
 *
 * @param filters - UI filter state.
 * @returns The where clause. Empty filters give an empty object.
 *
 * @example
 * constructPropertyWhere({ epcRatings: ['A', 'B'], propertyList: 'list1' })
 * // { lists: { idIn: ['list1'] }, epcRatingIn: ['A', 'B'] }
 */
export const constructPropertyWhere = (
  filters: PropertyFilters
): PropertyWhere => {
  const propertyWhere: PropertyWhere = {}

  // Property list relation: backend accepts lists.idIn
  if (filters.propertyList) {
    propertyWhere.lists = { idIn: [filters.propertyList] }
  }

  if (filters.propertyTypes && filters.propertyTypes.length > 0) {
    propertyWhere.typeIn = [...filters.propertyTypes]
  }

  if (filters.propertyForms && filters.propertyForms.length > 0) {
    propertyWhere.formIn = [...filters.propertyForms]
  }

  if (filters.ageBands && filters.ageBands.length > 0) {
    propertyWhere.ageIn = [...filters.ageBands]
  }

  if (filters.epcRatings && filters.epcRatings.length > 0) {
    propertyWhere.epcRatingIn = [...filters.epcRatings]
  }

  // Has model filter (supports both hasModel and has3D for backward compatibility)
  if (filters.hasModel !== '' && filters.hasModel !== undefined) {
    propertyWhere.isModelAvailable = filters.hasModel === 'true'
  } else if (filters.has3D === true) {
    // Only include this filter when explicitly enabled; do not send false by default
    propertyWhere.isModelAvailable = true
  }

  if (filters.addressSearch && filters.addressSearch.trim() !== '') {
    propertyWhere.address = { combinedLike: filters.addressSearch.trim() }
  }

  return propertyWhere
}

/**
 * Fetches property lists as dropdown options, sorted by name.
 *
 * Falls back to mock lists when the app runs without a real API.
 *
 * @param options - Optional filter.
 * @param options.organisationId - Limits the lists to one organisation.
 * @returns Options with the list id as `value` and its name as `label`.
 * @throws {Error} When the request fails and mock data is not available.
 */
export const fetchPropertyLists = async ({
  organisationId
}: { readonly organisationId?: string } = {}): Promise<
  readonly PropertyListOption[]
> => {
  try {
    const data = await executeGraphQL(
      PROPERTY_LISTS_QUERY,
      {
        where: organisationId
          ? { organisation: { idIn: [organisationId] } }
          : undefined
      },
      bearerAuth()
    )

    return (data.propertyLists ?? [])
      .filter(isPresent)
      .map((list) => ({ value: list.id, label: list.name ?? list.id }))
      .sort((a, b) => a.label.localeCompare(b.label))
  } catch (error) {
    console.error('Error fetching property lists:', error)
    return handleMockDataFallback(() => mockPropertyLists, 'property lists')
  }
}

/**
 * Fetches one page of properties for the portfolio table.
 *
 * Returns an empty page when the request fails.
 *
 * @param filters - UI filter state.
 * @param pageLimit - Properties per page.
 * @param sortField - Order condition, or null for the API default.
 * @param sortDirection - Sort direction, or null for the API default.
 * @param page - Zero-based page number.
 * @returns The page's rows (processed for the table), total count, and whether more pages exist.
 */
export const fetchTableProperties = async (
  filters: PropertyFilters = {},
  pageLimit = 20,
  sortField: PropertyOrderCondition | null = null,
  sortDirection: Sort_Order | null = null,
  page = 0
): Promise<PropertyPage<ReturnType<typeof processTableProperties>[number]>> => {
  try {
    const { data, extensions } = await executeGraphQLWithExtensions(
      PROPERTY_TABLE_PAGE_QUERY,
      {
        where: constructSortedPropertyWhere(filters, sortField, sortDirection),
        paging: { limit: pageLimit, page }
      },
      bearerAuth()
    )
    if (!data.properties) {
      return emptyPage()
    }

    return {
      ...readPageInfo(extensions),
      properties: processTableProperties(data.properties.filter(isPresent))
    }
  } catch (error) {
    console.error('Error fetching properties (table):', error)
    return emptyPage()
  }
}

/**
 * Fetches one page of minimal property rows for map markers.
 *
 * Returns an empty page when the request fails.
 *
 * @param filters - UI filter state.
 * @param pageLimit - Properties per page.
 * @param sortField - Order condition, or null for the API default.
 * @param sortDirection - Sort direction, or null for the API default.
 * @param page - Zero-based page number.
 * @returns The page's rows, total count, and whether more pages exist.
 */
export const fetchMapMarkers = async (
  filters: PropertyFilters = {},
  pageLimit = 100,
  sortField: PropertyOrderCondition | null = null,
  sortDirection: Sort_Order | null = null,
  page = 0
): Promise<PropertyPage<MapMarkerProperty>> => {
  try {
    const { data, extensions } = await executeGraphQLWithExtensions(
      PROPERTY_MAP_MARKERS_PAGE_QUERY,
      {
        where: constructSortedPropertyWhere(filters, sortField, sortDirection),
        paging: { limit: pageLimit, page }
      },
      bearerAuth()
    )
    if (!data.properties) {
      return emptyPage()
    }

    return {
      ...readPageInfo(extensions),
      properties: data.properties.filter(isPresent)
    }
  } catch (error) {
    console.error('Error fetching properties (mapMarkers):', error)
    return emptyPage()
  }
}

/**
 * Fetches one property for the map overlay, with ratings and heating demand pulled out.
 *
 * @param propertyId - The property to fetch.
 * @returns Overlay fields plus the raw property as `fullProperty`.
 * @throws {Error} When the request fails or the property does not exist.
 */
export const fetchPropertyForOverlay = async (
  propertyId: string
): Promise<PropertyOverlay> => {
  try {
    const data = await executeGraphQL(
      PROPERTY_OVERLAY_QUERY,
      { where: { id: propertyId } },
      bearerAuth()
    )
    const property = data.property
    if (!property) {
      throw new Error('Property not found')
    }

    const inspectionData = extractInspectionData(property)
    return {
      id: property.id,
      type: property.type,
      address: property.address?.[0]?.combined ?? '',
      epcRating: inspectionData.epcRating,
      predictedEpcRating: inspectionData.predictedEpcRating,
      kestrixSpaceHeatingDemand: inspectionData.kestrixSpaceHeatingDemand,
      epcSpaceHeatingDemand: inspectionData.epcSpaceHeatingDemand,
      modelId: property.models?.[0]?.modelId ?? null,
      fullProperty: {
        address: property.address,
        kestrixInspections: property.kestrixInspections,
        epcInspections: property.epcInspections,
        predictedInspections: property.predictedInspections,
        models: property.models,
        id: property.id,
        type: property.type,
        units: property.units
      }
    }
  } catch (error) {
    console.error('Error fetching property for overlay:', error)
    throw error
  }
}

/**
 * Fetches full property detail: units, inspections, models, annotations and recommendations.
 *
 * PV data is not included. Use {@link fetchPropertyPV}.
 *
 * On failure, a mock property with the same id is returned when one exists.
 * The mock does not have the query's shape.
 *
 * @param propertyId - The property to fetch.
 * @returns The property, a mock property, or null when the property does not exist.
 * @throws {Error} When the request fails and no mock property matches.
 */
export const fetchPropertyDetail = async (
  propertyId: string
): Promise<PropertyDetail | ReturnType<typeof enhanceMockProperty> | null> => {
  try {
    const data = await executeGraphQL(
      PROPERTY_DETAIL_QUERY,
      { where: { id: propertyId } },
      bearerAuth()
    )
    return data.property ?? null
  } catch (error) {
    console.error('Error fetching property detail (no paging):', error)
    const mockProperty = mockProperties.find((p) => p.id === propertyId)
    if (mockProperty) {
      return enhanceMockProperty(mockProperty, propertyId)
    }
    throw error
  }
}

/**
 * Fetches a property's PV output on its own, so the detail query stays light.
 *
 * @param propertyId - The property to fetch.
 * @returns Panel count, peak kW and annual kWh, or null when the property has no PV data
 *   or only exists in mock data.
 * @throws {Error} When the request fails and no mock property matches.
 */
export const fetchPropertyPV = async (
  propertyId: string
): Promise<PropertyPv | null> => {
  try {
    const data = await executeGraphQL(
      PROPERTY_PV_QUERY,
      { where: { id: propertyId } },
      bearerAuth()
    )
    if (!data.property) {
      throw new Error('Property not found')
    }
    return data.property.pv ?? null
  } catch (error) {
    console.error('Error fetching property PV data:', error)
    const mockProperty = mockProperties.find((p) => p.id === propertyId)
    if (mockProperty) {
      // Mock properties carry no PV data.
      return null
    }
    throw new Error('Property not found in mock data')
  }
}

let frontendConfigCache: FrontendConfig | null = null

/**
 * Fetches frontend configuration (Mapbox, Street View and OS keys, environment) once per session.
 *
 * @returns The config, or null when the API returns none.
 * @throws {Error} When the request fails.
 */
export const fetchFrontendConfig = async (): Promise<FrontendConfig | null> => {
  if (frontendConfigCache) return frontendConfigCache

  const data = await executeGraphQL(FE_CONFIG_QUERY, {}, bearerAuth())
  frontendConfigCache = data.feConfig ?? null
  return frontendConfigCache
}
